import * as React from "react";
import { cn } from "@/lib/utils";
import { WizardStepper } from "./WizardStepper";
import { Button } from "@/components/Base/Button";
import { Dialog, DialogContent } from "@/components/Base/Dialog";

export interface WizardProps {
  title: string;
  children: React.ReactNode;
  currentStep: number;
  totalSteps: number;
  onNext?: () => void;
  onBack?: () => void;
  onComplete?: () => void;
  className?: string;
  isDialog?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  nextLabel?: string;
  backLabel?: string;
  completeLabel?: string;
  hideBackOnFirstStep?: boolean;
  footer?: React.ReactNode;
}

export const Wizard = React.forwardRef<HTMLDivElement, WizardProps>(
  (
    {
      title,
      children,
      currentStep,
      totalSteps,
      onNext,
      onBack,
      onComplete,
      className,
      isDialog = false,
      open,
      onOpenChange,
      nextLabel = "Continue",
      backLabel = "Back",
      completeLabel = "Complete",
      hideBackOnFirstStep = true,
      footer,
    },
    ref
  ) => {
    const isLastStep = currentStep === totalSteps - 1;
    const isFirstStep = currentStep === 0;

    const content = (
      <div
        ref={ref}
        className={cn(
          "h-full w-full overflow-hidden rounded-xl border-2 border-white bg-primary text-primary-foreground",
          className
        )}
      >
        <div className={"flex h-full w-full flex-col bg-secondary/20 p-8"}>
          {/* Header */}
          <div className="mb-6 text-center">
            <h2 className="text-3xl font-extrabold text-primary-foreground">
              {title}
            </h2>
          </div>

          {/* Content */}
          <div className="mb-8 flex-1 overflow-auto">{children}</div>

          {/* Footer */}
          <div className="mb-4">{footer}</div>
          <div className="grid w-full grid-cols-5 items-center justify-between">
            <div className="col-span-1 flex flex-row items-center justify-start">
              {(!hideBackOnFirstStep || !isFirstStep) && (
                <Button
                  variant="outline"
                  onClick={onBack}
                  disabled={isFirstStep && hideBackOnFirstStep}
                >
                  {backLabel}
                </Button>
              )}
            </div>
            <div className="col-span-3 flex flex-row items-center justify-center">
              <WizardStepper
                totalSteps={totalSteps}
                currentStep={currentStep}
                className="mx-4"
              />
            </div>
            <div className="col-span-1 flex flex-row items-center justify-end">
              <Button
                variant="accent"
                onClick={isLastStep ? onComplete : onNext}
              >
                {isLastStep ? completeLabel : nextLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );

    if (isDialog) {
      return (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-3xl border-0 bg-transparent p-0">
            {content}
          </DialogContent>
        </Dialog>
      );
    }

    return content;
  }
);

Wizard.displayName = "Wizard";
